import { useEffect } from 'react';
import { Box, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CopyrightIcon from '@mui/icons-material/Copyright';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PaymentsIcon from '@mui/icons-material/Payments';
import PolicyIcon from '@mui/icons-material/Policy';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import BalanceIcon from '@mui/icons-material/Balance';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

function Legal() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const legalSections = [
    {
      icon: <GavelIcon />,
      title: 'Agreement to Terms',
      content: "By accessing or using TopSWE's website and services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services."
    },
    {
      icon: <CopyrightIcon />,
      title: 'Intellectual Property Rights',
      content: 'The content on TopSWE, including but not limited to text, graphics, code, videos, and teaching materials, is owned by TopSWE and protected by intellectual property laws. You may not:',
      bullets: [
        'Redistribute or sell our content',
        'Share your account access with others',
        'Copy or duplicate our proprietary problem-solving templates',
        'Record or redistribute our video content',
        'Use our content for commercial purposes without written permission'
      ]
    },
    {
      icon: <AccountCircleIcon />,
      title: 'User Accounts',
      content: 'When you create an account with us, you must provide accurate information. You are responsible for:',
      bullets: [
        'Maintaining the security of your account credentials',
        'All activities that occur under your account',
        'Notifying us immediately of any unauthorized access',
        'Ensuring your account information remains current'
      ]
    },
    {
      icon: <PaymentsIcon />,
      title: 'Payment Terms',
      content: 'By purchasing a subscription or lifetime access:',
      bullets: [
        'You agree to pay the fees as presented at time of purchase',
        'All payments are processed securely through Stripe',
        'Lifetime access means for the lifetime of the service',
        'We reserve the right to modify pricing for new customers',
        'Existing lifetime members will maintain their access at their original purchase price'
      ]
    },
    {
      icon: <PolicyIcon />,
      title: 'Refund Policy',
      content: 'We do not offer refunds.',
      highlight: true
    },
    {
      icon: <BuildIcon />,
      title: 'Service Modifications',
      content: 'We reserve the right to:',
      bullets: [
        'Modify or discontinue any part of our service',
        'Add new features or content',
        'Update problem solutions and learning materials',
        'Change pricing for new customers'
      ]
    },
    {
      icon: <PersonIcon />,
      title: 'User Conduct',
      content: 'You agree not to:',
      bullets: [
        'Share solutions or answers publicly',
        'Attempt to bypass any access restrictions',
        'Use automated systems to access our content',
        'Engage in any activity that disrupts our services',
        'Share account access with others'
      ]
    },
    {
      icon: <WarningIcon />,
      title: 'Disclaimer',
      content: 'While we strive to provide the best preparation materials:',
      bullets: [
        'We do not guarantee job placement',
        'Interview success depends on multiple factors',
        'Problem-solving capabilities vary by individual',
        'Company interview processes may change'
      ]
    },
    {
      icon: <WarningIcon />,
      title: 'Limitation of Liability',
      content: 'TopSWE shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services.'
    },
    {
      icon: <BalanceIcon />,
      title: 'Governing Law',
      content: 'These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.'
    },
    {
      icon: <PolicyIcon />,
      title: 'Changes to Terms',
      content: 'We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through our website.'
    },
    {
      icon: <ContactSupportIcon />,
      title: 'Contact Information',
      content: 'For any questions about these Terms, please contact us at:',
      contact: 'topswebusiness@gmail.com'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        component="section"
        sx={{
          background: (theme) => theme.palette.custom.heroGradient,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 8, md: 11 }
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Chip label="Last updated: March 2024" />
            <Typography variant="h2" sx={{ fontWeight: 850, letterSpacing: 0, fontSize: { xs: '2.7rem', md: '4.7rem' } }}>
              Terms of Service
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 720, lineHeight: 1.55, fontWeight: 450 }}>
              The terms that govern access to TopSWE courses, practice, and account features.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={2.5}>
          {legalSections.map((section) => (
            <Grid item xs={12} md={6} key={section.title}>
              <Paper
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderColor: section.highlight ? 'warning.main' : 'divider'
                }}
              >
                <Stack spacing={2.25}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2.5,
                        bgcolor: section.highlight ? 'rgba(183, 121, 31, 0.12)' : 'action.hover',
                        color: section.highlight ? 'warning.main' : 'secondary.main',
                        display: 'grid',
                        placeItems: 'center'
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {section.title}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {section.content}
                  </Typography>

                  {section.bullets && (
                    <Stack spacing={1.25}>
                      {section.bullets.map((bullet) => (
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" key={bullet}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: section.highlight ? 'warning.main' : 'secondary.main',
                              mt: 1,
                              flexShrink: 0
                            }}
                          />
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                            {bullet}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {section.contact && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', boxShadow: 'none', textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 750 }}>
                        {section.contact}
                      </Typography>
                    </Paper>
                  )}

                  {section.highlight && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(183, 121, 31, 0.1)', boxShadow: 'none' }}>
                      <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 750, textAlign: 'center' }}>
                        Important policy
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ mt: 3, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            By using TopSWE, you acknowledge that you have read, understood, and agreed to be bound by these Terms of Service.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default Legal;
